// RITA-1533 — Reine Regeln für den Platz der Platzhalterleiste, ohne DOM-Zugriff: Zahl der Chipreihen der
// kompakten Leiste oben (Leiste oben) und die wirksame Lage aus gewählter Lage und Rahmenbreite (V8).

export type Leistenposition = 'links' | 'rechts' | 'oben';

export const LEISTENPOSITIONEN: readonly Leistenposition[] = ['links', 'oben', 'rechts'];
export const LEISTE_STANDARDPOSITION: Leistenposition = 'links';
/** Schlüssel im localStorage des Browsers; die Wahl gilt je Rechner und Browser. */
export const LEISTE_SPEICHERSCHLUESSEL = 'vertragseditor.platzhalterleiste.position';

export function istLeistenposition(wert: unknown): wert is Leistenposition {
  return typeof wert === 'string' && (LEISTENPOSITIONEN as readonly string[]).includes(wert);
}

/** Maße der kompakten Leiste oben (Chromium 08.10.2026): Innenabstand, Kopfzeile ohne Hinweiszeile, Abstand zum Chipbereich. */
export const LEISTE_INNENABSTAND_PX = 12;
export const LEISTE_KOPFZEILE_PX = 46;
export const LEISTE_KOPFABSTAND_PX = 4;
/** Chip 24 px hoch, 4 px Lücke → eine Chipreihe belegt 28 px. */
export const CHIP_LUECKE_PX = 4;
export const CHIPREIHE_PX = 24 + CHIP_LUECKE_PX;
/** So viel Vertrag soll unter der Leiste mindestens sichtbar bleiben; sonst reicht eine Chipreihe (Entscheidung Julia 07.10.2026). */
export const MINDEST_VERTRAG_SICHTBAR_PX = 300;

/** Gesamthöhe der kompakten Leiste oben aus gemessener Kopfzeile (mit umgebrochenen Hinweisen) und Reihenzahl. */
export function leisteObenHoehePx(kopfzeileHoehePx: number, reihen: 1 | 2): number {
  return 2 * LEISTE_INNENABSTAND_PX + kopfzeileHoehePx + LEISTE_KOPFABSTAND_PX + reihen * CHIPREIHE_PX - CHIP_LUECKE_PX;
}

/** Höhe mit zwei Reihen und einzeiliger Kopfzeile: 126 px (Kollegenreview !765: mit Hinweiszeile ≈ 148 px). */
export const LEISTE_OBEN_HOEHE_ZWEI_REIHEN_PX = leisteObenHoehePx(LEISTE_KOPFZEILE_PX, 2);

/**
 * Zahl der Chipreihen der kompakten Leiste oben: zwei, solange darunter mindestens MINDEST_VERTRAG_SICHTBAR_PX
 * vom Vertrag sichtbar bleiben, sonst eine. Eingänge sind Fensterhöhe, feste Zone bis zur Leiste und die
 * GEMESSENE Kopfzeilenhöhe (umgebrochene Hinweise zählen mit; Kollegenreview !765), nicht die Scrollposition
 * (kein Hin und Her beim Scrollen). Ohne messbare Fensterhöhe (Druck, jsdom ohne Layout) gelten zwei Reihen.
 */
export function kompakteReihen(
  fensterHoehe: number | null | undefined,
  leisteOben: number,
  kopfzeileHoehePx: number = LEISTE_KOPFZEILE_PX,
): 1 | 2 {
  if (!fensterHoehe || fensterHoehe <= 0) return 2;
  const sichtbar = fensterHoehe - leisteOben - leisteObenHoehePx(kopfzeileHoehePx, 2);
  return sichtbar < MINDEST_VERTRAG_SICHTBAR_PX ? 1 : 2;
}

/** Innenabstand des Seitenrahmens je Seite (VertragArbeitsbereich `p: 3`) und Lücke im Flex-Rahmen (`gap: 2`). */
export const SEITENRAHMEN_INNENABSTAND_PX = 24;
export const LEISTEN_LUECKE_PX = 16;
/**
 * Spielraum für den Weg zurück an die Seite: Mit der Lage kann eine Bildlaufleiste erscheinen oder verschwinden
 * und den Rahmen um ihre Breite ändern; ohne Spielraum spränge die Leiste an der Grenze hin und her (AK25).
 */
export const LEISTEN_HYSTERESE_PX = 24;

/** Rahmenbreite, ab der die Leiste neben dem ganz sichtbaren Blatt Platz hat (ohne Querscrollen). */
export function leistenBedarfPx(seiteBreitePx: number, leisteBreitePx: number): number {
  return seiteBreitePx + 2 * SEITENRAHMEN_INNENABSTAND_PX + LEISTEN_LUECKE_PX + leisteBreitePx;
}

/**
 * Wirksame Lage der Leiste (V8, Variante A – Entscheidung Julia 05./07.10.2026): „oben“ gewählt bleibt oben.
 * Seitlich gewählt gilt seitlich, solange der Rahmen den Bedarf hergibt; sonst steht die Leiste oben (mit
 * Hinweis) und kehrt zurück, sobald der Platz samt Spielraum reicht. Der Spielraum gilt nur für den Rückweg aus
 * einer ERZWUNGENEN Lage oben (`bisherErzwungen`), nicht nach einer eigenen Wahl „oben“ – sonst stünde die
 * Leiste nach dem Umschalten auf „links“ in einem 24-px-Band mit falschem Hinweis weiter oben (Review-Befund B2).
 * Ohne messbare Breite (null/0: vor der ersten Messung, jsdom) gilt die gewählte Lage; im Druck behält der Hook
 * die letzte Bildschirmmessung.
 */
export function wirksameLeistenposition(
  gewaehlt: Leistenposition,
  rahmenBreitePx: number | null,
  bedarfPx: number,
  bisherErzwungen: boolean,
): Leistenposition {
  if (gewaehlt === 'oben') return 'oben';
  if (!rahmenBreitePx || rahmenBreitePx <= 0) return gewaehlt;
  const grenze = bisherErzwungen ? bedarfPx + LEISTEN_HYSTERESE_PX : bedarfPx;
  return rahmenBreitePx >= grenze ? gewaehlt : 'oben';
}

/**
 * RITA-1585: Mindestbreite des Inhalts neben der Handbuch-Spalte – das ganze Blatt samt Seitenrahmen, ohne Querscrollen.
 * Die Platzhalterleiste zählt nicht mit: Sie rückt bei Platzmangel von selbst nach oben (`wirksameLeistenposition`).
 */
export function inhaltMindestbreitePx(seiteBreitePx: number): number {
  return seiteBreitePx + 2 * SEITENRAHMEN_INNENABSTAND_PX;
}
